import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  IconButton,
  Image,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  SimpleGrid,
  Flex,
  Divider,
} from '@chakra-ui/react';
import { 
  FaPlay, 
  FaSpotify, 
  FaApple, 
  FaMusic,
  FaDollarSign,
  FaCalendarAlt,
  FaTag
} from 'react-icons/fa';
import { SlBasket } from 'react-icons/sl';
import { BeatIcon } from '../../tools/animatedIcons';
import { useMusicPlayer } from '../../context/MusicPlayerContext';
import { useCart } from '../../context/CartContext';

const TrackOverview = ({ trackInfo }) => {
  const { playTrack, isPlaying, currentTrack } = useMusicPlayer();
  const { addToCart } = useCart();

  if (!trackInfo) return null;

  const cardBg = 'dark.100';
  const borderColor = 'dark.900';
  const textColor = 'primary.500';
  const mutedTextColor = 'secondary.900';
  const headingColor = 'primary.100';

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
      <CardBody p={0}>
        <Flex direction={{ base: 'column', lg: 'row' }} height={{ base: 'auto', lg: '300px' }}>
          {/* Track Image */}
          <Box 
            flex={{ base: 'none', lg: '0 0 300px' }}
            height={{ base: '250px', lg: '100%' }}
            position="relative"
            overflow="hidden"
          >
            <Image
              src={trackInfo.image_url}
              alt={trackInfo.track_name}
              width="100%"
              height="100%"
              objectFit="cover"
            />
            
            {/* Play Button Overlay */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
            >
              <Button
                leftIcon={
                  isPlaying && currentTrack.name === trackInfo.track_name ? (
                    <BeatIcon />
                  ) : (
                    <FaPlay />
                  )
                }
                isDisabled={!trackInfo.preview_url && !trackInfo.track_url}
                onClick={() =>
                  playTrack({
                    name: trackInfo.track_name,
                    artist: trackInfo.artist_name,
                    image: trackInfo.image_url,
                    url: trackInfo.preview_url || trackInfo.track_url,
                    track_id: trackInfo.track_id,
                    artist_id: trackInfo.publisher?.id,
                  })
                }
                size="lg"
                variant="solid"
                bg="secondary.500"
                color="dark.900"
                _hover={{ bg: 'secondary.600' }}
                borderRadius="full"
                boxShadow="lg"
              >
                {isPlaying && currentTrack.name === trackInfo.track_name
                  ? 'Playing...'
                  : 'Preview'}
              </Button>
            </Box>
          </Box>

          {/* Track Details */}
          <Box flex="1" p={6}>
            <VStack spacing={4} align="start" height="100%">
              {/* Title and Artist */}
              <VStack spacing={1} align="start">
                <Heading as="h1" size="xl" color={headingColor} lineHeight="short">
                  {trackInfo.track_name}
                </Heading>
                <Text fontSize="lg" color="primary.900" fontWeight="medium">
                  {trackInfo.artist_name}
                </Text>
                <Text fontSize="md" color={mutedTextColor}>
                  {trackInfo.album_name} • {trackInfo.album_type}
                </Text>
              </VStack>

              {/* Key Metrics */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} width="100%">
                <Stat>
                  <StatLabel color="primary.500" fontSize="xs">PRICE PER SHARE</StatLabel>
                  <StatNumber color="primary.900" fontSize="lg">
                    ${trackInfo.price_per_share}
                  </StatNumber>
                </Stat>
                
                <Stat>
                  <StatLabel color="primary.500" fontSize="xs">AVAILABLE SHARES</StatLabel>
                  <StatNumber color={textColor} fontSize="lg">
                    {trackInfo.available_shares?.toLocaleString() || 'N/A'}
                  </StatNumber>
                </Stat>
                
                <Stat>
                  <StatLabel color="primary.500" fontSize="xs">RELEASE DATE</StatLabel>
                  <StatNumber color={textColor} fontSize="lg">
                    {new Date(trackInfo.release_date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </StatNumber>
                </Stat>

                {trackInfo.spotify_popularity && (
                  <Stat>
                    <StatLabel color="primary.500" fontSize="xs">SPOTIFY POPULARITY</StatLabel>
                    <StatNumber color="green.400" fontSize="lg">
                      {trackInfo.spotify_popularity}/100
                    </StatNumber>
                  </Stat>
                )}
              </SimpleGrid>

              <Divider borderColor="dark.900" />

              {/* Streaming Platforms and Actions */}
              <Flex justify="space-between" align="center" width="100%" flexWrap="wrap" gap={4}>
                {/* Streaming Platforms */}
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" color={mutedTextColor} fontWeight="medium">
                    Available On
                  </Text>
                  <HStack spacing={2}>
                    {trackInfo.streaming_platforms?.map((platform, idx) => (
                      <IconButton
                        key={idx}
                        size="sm"
                        icon={
                          platform.platform === 'spotify' ? (
                            <FaSpotify />
                          ) : (
                            <FaApple />
                          )
                        }
                        aria-label={platform.platform}
                        onClick={() => {
                          const url =
                            platform.platform === 'spotify'
                              ? `https://open.spotify.com/track/${platform.id}`
                              : `https://music.apple.com/us/song/${trackInfo.track_name}/${platform.id}`;
                          window.open(url, '_blank');
                        }}
                        variant="outline"
                        colorScheme={platform.platform === 'spotify' ? 'green' : 'red'}
                        _hover={{ 
                          bg: platform.platform === 'spotify' ? 'green.500' : 'secondary.500',
                          color: 'white'
                        }}
                      />
                    ))}
                  </HStack>
                </VStack>

                {/* Actions */}
                <VStack align="end" spacing={2}>
                  <Text fontSize="sm" color={mutedTextColor} fontWeight="medium">
                    Actions
                  </Text>
                  <Button
                    leftIcon={<SlBasket />}
                    onClick={() => addToCart(trackInfo)}
                    variant="solid"
                    bg="secondary.500"
                    color="dark.900"
                    _hover={{ bg: 'secondary.600' }}
                    size="sm"
                  >
                    Add to Basket
                  </Button>
                </VStack>
              </Flex>
            </VStack>
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default TrackOverview;